// src/pages/AdminDashboard.jsx

import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  alpha,
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BusinessIcon from "@mui/icons-material/Business";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import EuroRoundedIcon from "@mui/icons-material/EuroRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import PendingActionsRoundedIcon from "@mui/icons-material/PendingActionsRounded";
import PersonIcon from "@mui/icons-material/Person";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../contexts/firebase/config";

const MS_DIA = 1000 * 60 * 60 * 24;

function parseDate(value, endOfDay = false) {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;

  if (typeof value === "string") {
    const partes = value.split("/");

    if (partes.length === 3) {
      const [dia, mes, ano] = partes.map(Number);
      if (!dia || !mes || !ano) return null;

      return new Date(
        ano,
        mes - 1,
        dia,
        endOfDay ? 23 : 0,
        endOfDay ? 59 : 0,
        endOfDay ? 59 : 0
      );
    }
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function diasAte(value) {
  const data = parseDate(value);
  if (!data) return null;

  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  const alvo = new Date(data);
  alvo.setHours(0, 0, 0, 0);

  return Math.ceil((alvo - hoje) / MS_DIA);
}

function obterEstadoPagamento(ref) {
  const estado = (ref?.estado || "pendente").toString().toLowerCase();
  if (estado === "pago") return "pago";

  const validadeDate = parseDate(ref?.validade, true);
  if (validadeDate && validadeDate < new Date()) return "expirado";

  return estado;
}

function money(value) {
  return new Intl.NumberFormat("pt-PT", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function normalizar(value) {
  return (value || "").toString().toLowerCase().trim();
}

function percentagem(parte, total) {
  if (!total) return 0;
  return Math.min(100, Math.max(0, Math.round((parte / total) * 100)));
}

function countBy(list, predicate) {
  return list.filter(predicate).length;
}

export default function AdminDashboard() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const ui = {
    bg: isDark
      ? "radial-gradient(circle at top left, rgba(59,130,246,0.18), transparent 34%), radial-gradient(circle at top right, rgba(16,185,129,0.10), transparent 28%), #020617"
      : "radial-gradient(circle at top left, rgba(37,99,235,0.12), transparent 36%), radial-gradient(circle at top right, rgba(16,185,129,0.10), transparent 30%), #f6f8fc",
    card: isDark ? "rgba(15,23,42,0.88)" : "rgba(255,255,255,0.92)",
    soft: isDark ? "rgba(255,255,255,0.055)" : "rgba(248,250,252,0.92)",
    border: isDark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.08)",
    text: theme.palette.text.primary,
    muted: theme.palette.text.secondary,
  };

  const [empresas, setEmpresas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loadingEmpresas, setLoadingEmpresas] = useState(true);
  const [loadingClientes, setLoadingClientes] = useState(true);
  const [error, setError] = useState("");

  const loading = loadingEmpresas || loadingClientes;

  useEffect(() => {
    setError("");

    const unsubEmpresas = onSnapshot(
      collection(db, "empresas"),
      (snap) => {
        setEmpresas(
          snap.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
        );
        setLoadingEmpresas(false);
      },
      () => {
        setError("Não foi possível carregar as empresas.");
        setLoadingEmpresas(false);
      }
    );

    const unsubClientes = onSnapshot(
      collection(db, "clientes"),
      (snap) => {
        setClientes(
          snap.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
        );
        setLoadingClientes(false);
      },
      () => {
        setError("Não foi possível carregar os clientes.");
        setLoadingClientes(false);
      }
    );

    return () => {
      unsubEmpresas();
      unsubClientes();
    };
  }, []);

  const clientesInfo = useMemo(() => {
    const ativos = countBy(clientes, (cliente) => {
      const estado = normalizar(cliente.estado);
      const dias = diasAte(cliente.dataFim);
      return estado.includes("ativo") || (dias !== null && dias >= 0);
    });

    const expirados = countBy(clientes, (cliente) => {
      const estado = normalizar(cliente.estado);
      const dias = diasAte(cliente.dataFim);
      return estado.includes("expir") || (dias !== null && dias < 0);
    });

    const semEmail = countBy(clientes, (cliente) => !cliente.email);

    const proximos30 = countBy(clientes, (cliente) => {
      const dias = diasAte(cliente.dataFim);
      return dias !== null && dias >= 0 && dias <= 30;
    });

    return { ativos, expirados, semEmail, proximos30 };
  }, [clientes]);

  const pagamentosInfo = useMemo(() => {
    const refs = clientes.filter((cliente) => cliente.referenciaPagamento);

    const porEstado = refs.reduce(
      (acc, cliente) => {
        const estado = obterEstadoPagamento(cliente.referenciaPagamento);
        const valor = Number(cliente.referenciaPagamento?.valor || 0);

        if (estado === "pago") {
          acc.pagos += 1;
          acc.totalPago += valor;
        } else if (estado === "expirado") {
          acc.expirados += 1;
          acc.totalExpirado += valor;
        } else {
          acc.pendentes += 1;
          acc.totalPendente += valor;
        }

        return acc;
      },
      {
        pendentes: 0,
        pagos: 0,
        expirados: 0,
        totalPendente: 0,
        totalPago: 0,
        totalExpirado: 0,
      }
    );

    return {
      total: refs.length,
      ...porEstado,
    };
  }, [clientes]);

  const taxaClientesAtivos = percentagem(clientesInfo.ativos, clientes.length);
  const taxaRenovacoes = percentagem(clientesInfo.proximos30, clientes.length);
  const taxaSemEmail = percentagem(clientesInfo.semEmail, clientes.length);
  const taxaPagos = percentagem(pagamentosInfo.pagos, pagamentosInfo.total);

  const alertas = [
    {
      label: "Renovações próximas",
      value: clientesInfo.proximos30,
      color: theme.palette.warning.main,
      icon: <EventAvailableRoundedIcon />,
      helper: "clientes terminam nos próximos 30 dias",
    },
    {
      label: "Pagamentos pendentes",
      value: pagamentosInfo.pendentes,
      color: theme.palette.error.main,
      icon: <PendingActionsRoundedIcon />,
      helper: money(pagamentosInfo.totalPendente),
    },
    {
      label: "Clientes sem email",
      value: clientesInfo.semEmail,
      color: theme.palette.info.main,
      icon: <EmailRoundedIcon />,
      helper: "precisam de contacto atualizado",
    },
  ];

  const metricCards = [
    {
      title: "Clientes",
      value: clientes.length,
      subtitle: `${clientesInfo.ativos} ativos · ${clientesInfo.expirados} expirados`,
      icon: <PersonIcon />,
      color: theme.palette.primary.main,
      to: "/admin/clientes",
      link: "Ver clientes",
    },
    {
      title: "Parceiros",
      value: empresas.length,
      subtitle: "Empresas registadas",
      icon: <BusinessIcon />,
      color: theme.palette.success.main,
      to: "/admin/parceiros",
      link: "Ver parceiros",
    },
    {
      title: "Pagamentos",
      value: pagamentosInfo.total,
      subtitle: `${pagamentosInfo.pagos} pagos · ${pagamentosInfo.pendentes} pendentes`,
      icon: <CreditCardIcon />,
      color: theme.palette.warning.main,
      to: "/admin/pagamentos",
      link: "Ver pagamentos",
    },
    {
      title: "Receita Confirmada",
      value: money(pagamentosInfo.totalPago),
      subtitle: `${taxaPagos}% das referências pagas`,
      icon: <EuroRoundedIcon />,
      color: theme.palette.info.main,
      to: "/admin/relatorio",
      link: "Ver relatório",
    },
  ];

  const financialCards = [
    {
      title: "Valor Pendente",
      value: pagamentosInfo.totalPendente,
      subtitle: `${pagamentosInfo.pendentes} pagamentos`,
      icon: <PendingActionsRoundedIcon />,
      color: theme.palette.warning.main,
    },
    {
      title: "Receita Confirmada",
      value: pagamentosInfo.totalPago,
      subtitle: `${pagamentosInfo.pagos} pagamentos`,
      icon: <CheckCircleRoundedIcon />,
      color: theme.palette.success.main,
    },
    {
      title: "Valor Expirado",
      value: pagamentosInfo.totalExpirado,
      subtitle: `${pagamentosInfo.expirados} referências`,
      icon: <WarningAmberRoundedIcon />,
      color: theme.palette.error.main,
    },
  ];

  const operationalCards = [
    {
      title: "Clientes Ativos",
      value: clientesInfo.ativos,
      percent: taxaClientesAtivos,
      icon: <PersonIcon />,
      color: theme.palette.success.main,
    },
    {
      title: "Renovações em 30 dias",
      value: clientesInfo.proximos30,
      percent: taxaRenovacoes,
      icon: <EventAvailableRoundedIcon />,
      color: theme.palette.warning.main,
    },
    {
      title: "Clientes sem email",
      value: clientesInfo.semEmail,
      percent: taxaSemEmail,
      icon: <EmailRoundedIcon />,
      color: theme.palette.primary.main,
    },
  ];

  const cardSx = {
    height: "100%",
    borderRadius: "28px",
    bgcolor: ui.card,
    border: `1px solid ${ui.border}`,
    backdropFilter: "blur(16px)",
    boxShadow: isDark
      ? "0 22px 60px rgba(0,0,0,0.28)"
      : "0 22px 60px rgba(15,23,42,0.075)",
  };

  const MetricCard = ({ title, value, subtitle, icon, color, to, link }) => (
    <Card
      elevation={0}
      sx={{
        ...cardSx,
        overflow: "hidden",
        transition:
          "transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease",
        "&:hover": {
          transform: "translateY(-6px)",
          boxShadow: `0 28px 75px ${alpha(color, 0.18)}`,
          borderColor: alpha(color, 0.38),
        },
      }}
    >
      <CardActionArea component={RouterLink} to={to} sx={{ height: "100%" }}>
        <CardContent sx={{ p: { xs: 2.4, md: 2.8 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Avatar
              sx={{
                width: 56,
                height: 56,
                borderRadius: "20px",
                bgcolor: alpha(color, isDark ? 0.18 : 0.12),
                color,
              }}
            >
              {icon}
            </Avatar>

            <Chip
              size="small"
              label="Abrir"
              sx={{
                height: 28,
                borderRadius: 99,
                bgcolor: alpha(color, 0.1),
                color,
                fontWeight: 900,
              }}
            />
          </Stack>

          <Typography sx={{ mt: 2.3, fontWeight: 900, color: ui.text }}>
            {title}
          </Typography>

          <Typography
            sx={{
              mt: 1,
              fontSize: { xs: "1.8rem", md: "2.08rem" },
              fontWeight: 950,
              lineHeight: 1,
              letterSpacing: "-0.055em",
              color: ui.text,
            }}
          >
            {loading ? <Skeleton width={105} /> : value}
          </Typography>

          <Typography sx={{ mt: 1, minHeight: 20, fontSize: 13, color, fontWeight: 850 }}>
            {loading ? <Skeleton width="70%" /> : subtitle}
          </Typography>

          <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mt: 2.3 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 950, color }}>
              {link}
            </Typography>
            <ArrowForwardIcon sx={{ fontSize: 17, color }} />
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );

  const FinancialBox = ({ title, value, subtitle, icon, color }) => (
    <Paper
      elevation={0}
      sx={{
        p: 2.6,
        borderRadius: "24px",
        bgcolor: alpha(color, isDark ? 0.15 : 0.075),
        border: `1px solid ${alpha(color, 0.2)}`,
        height: "100%",
      }}
    >
      <Stack direction="row" spacing={1.4} alignItems="center" sx={{ mb: 2 }}>
        <Avatar
          sx={{
            width: 44,
            height: 44,
            borderRadius: "16px",
            bgcolor: alpha(color, 0.15),
            color,
          }}
        >
          {icon}
        </Avatar>

        <Box>
          <Typography sx={{ fontWeight: 900, color: ui.text }}>{title}</Typography>
          <Typography sx={{ fontSize: 12.5, color: ui.muted, fontWeight: 700 }}>
            {loading ? <Skeleton width={90} /> : subtitle}
          </Typography>
        </Box>
      </Stack>

      <Typography
        sx={{
          fontSize: { xs: "1.55rem", md: "1.85rem" },
          fontWeight: 950,
          letterSpacing: "-0.055em",
          color: ui.text,
        }}
      >
        {loading ? <Skeleton width={130} /> : money(value)}
      </Typography>
    </Paper>
  );

  const OperationalBox = ({ title, value, percent, icon, color }) => (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: "24px",
        bgcolor: ui.soft,
        border: `1px solid ${ui.border}`,
        height: "100%",
      }}
    >
      <Stack direction="row" spacing={1.4} alignItems="center">
        <Avatar
          sx={{
            width: 44,
            height: 44,
            borderRadius: "16px",
            bgcolor: alpha(color, 0.12),
            color,
          }}
        >
          {icon}
        </Avatar>

        <Box>
          <Typography sx={{ fontWeight: 900 }}>{title}</Typography>
          <Typography sx={{ mt: 0.8, fontSize: "1.85rem", fontWeight: 950, lineHeight: 1 }}>
            {loading ? <Skeleton width={58} /> : value}
          </Typography>
        </Box>
      </Stack>

      <Stack direction="row" justifyContent="space-between" sx={{ mt: 1.8 }}>
        <Typography sx={{ fontSize: 13, color: ui.muted, fontWeight: 800 }}>
          Percentagem do total
        </Typography>
        <Typography sx={{ fontSize: 13, color, fontWeight: 950 }}>
          {loading ? "..." : `${percent}%`}
        </Typography>
      </Stack>

      <LinearProgress
        variant="determinate"
        value={loading ? 0 : percent}
        sx={{
          mt: 1.1,
          height: 9,
          borderRadius: 99,
          bgcolor: alpha(color, 0.12),
          "& .MuiLinearProgress-bar": {
            borderRadius: 99,
            bgcolor: color,
          },
        }}
      />
    </Paper>
  );

  const Section = ({ title, subtitle, children, action }) => (
    <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2.2, md: 3 } }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1.2}
        sx={{ mb: 2.6 }}
      >
        <Box>
          <Typography sx={{ fontSize: "1.08rem", fontWeight: 950 }}>
            {title}
          </Typography>
          <Typography sx={{ mt: 0.45 }} variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>

        {action}
      </Stack>

      {children}
    </Paper>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: ui.bg,
        px: { xs: 2, md: 4 },
        py: { xs: 3, md: 5 },
      }}
    >
      <Box sx={{ maxWidth: 1340, mx: "auto" }}>
        <Paper
          elevation={0}
          sx={{
            ...cardSx,
            p: { xs: 2.6, md: 4 },
            mb: 2.8,
            overflow: "hidden",
            position: "relative",
            "&:before": {
              content: '""',
              position: "absolute",
              inset: 0,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.14
              )}, transparent 42%)`,
              pointerEvents: "none",
            },
          }}
        >
          <Stack
            direction={{ xs: "column", lg: "row" }}
            justifyContent="space-between"
            spacing={3}
            sx={{ position: "relative" }}
          >
            <Box sx={{ maxWidth: 720 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1.4 }}>
                <Chip
                  icon={<CalendarMonthRoundedIcon />}
                  label={new Date().toLocaleDateString("pt-PT", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                  })}
                  sx={{ borderRadius: "14px", bgcolor: ui.soft, fontWeight: 850 }}
                />
                <Chip
                  icon={<DashboardRoundedIcon />}
                  label={loading ? "A carregar dados" : "Dados em tempo real"}
                  sx={{
                    borderRadius: "14px",
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    color: theme.palette.success.main,
                    fontWeight: 950,
                  }}
                />
              </Stack>

              <Typography sx={{ fontSize: 15, color: ui.muted, fontWeight: 750 }}>
                Bem-vindo de volta, <strong>Bruno Rodrigues</strong> 👋
              </Typography>

              <Typography
                sx={{
                  mt: 0.7,
                  fontSize: { xs: "2.25rem", md: "3.35rem" },
                  fontWeight: 950,
                  letterSpacing: "-0.07em",
                  lineHeight: 0.98,
                  color: ui.text,
                }}
              >
                Painel Administrativo
              </Typography>

              <Typography sx={{ mt: 1.45, color: ui.muted, maxWidth: 670 }}>
                Acompanha clientes, parceiros, pagamentos e alertas operacionais num só
                lugar, com indicadores rápidos para decisões diárias.
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ mt: 2.4 }}>
                <Button
                  component={RouterLink}
                  to="/admin/clientes"
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ borderRadius: "14px", py: 1.15, fontWeight: 900 }}
                >
                  Gerir clientes
                </Button>
                <Button
                  component={RouterLink}
                  to="/admin/pagamentos"
                  variant="outlined"
                  sx={{ borderRadius: "14px", py: 1.15, fontWeight: 900 }}
                >
                  Ver pagamentos
                </Button>
              </Stack>
            </Box>

            <Grid container spacing={1.3} sx={{ maxWidth: { lg: 460 } }}>
              {alertas.map((alerta) => (
                <Grid item xs={12} sm={4} lg={12} key={alerta.label}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.7,
                      borderRadius: "20px",
                      bgcolor: alpha(alerta.color, isDark ? 0.13 : 0.075),
                      border: `1px solid ${alpha(alerta.color, 0.18)}`,
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.35}>
                      <Avatar
                        sx={{
                          width: 42,
                          height: 42,
                          borderRadius: "15px",
                          bgcolor: alpha(alerta.color, 0.14),
                          color: alerta.color,
                        }}
                      >
                        {alerta.icon}
                      </Avatar>

                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 950, color: ui.text }}>
                          {loading ? <Skeleton width={35} /> : alerta.value}{" "}
                          {alerta.label}
                        </Typography>
                        <Typography sx={{ fontSize: 12.5, color: ui.muted, fontWeight: 750 }}>
                          {loading ? <Skeleton width={150} /> : alerta.helper}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Stack>
        </Paper>

        {error && (
          <Alert severity="warning" sx={{ mb: 3, borderRadius: "18px" }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2.2}>
          {metricCards.map((card) => (
            <Grid item xs={12} sm={6} lg={3} key={card.title}>
              <MetricCard {...card} />
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2.2} sx={{ mt: 0.6 }}>
          <Grid item xs={12} lg={8}>
            <Section
              title="Resumo Financeiro"
              subtitle="Estado atual dos pagamentos na plataforma"
              action={
                <Chip
                  icon={<TrendingUpRoundedIcon />}
                  label={`${taxaPagos}% pagos`}
                  sx={{
                    borderRadius: "12px",
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    color: theme.palette.success.main,
                    fontWeight: 900,
                  }}
                />
              }
            >
              <Grid container spacing={2}>
                {financialCards.map((card) => (
                  <Grid item xs={12} md={4} key={card.title}>
                    <FinancialBox {...card} />
                  </Grid>
                ))}
              </Grid>
            </Section>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Section title="Saúde da Carteira" subtitle="Resumo rápido dos clientes">
              <Stack spacing={1.7}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary" fontWeight={800}>
                    Clientes ativos
                  </Typography>
                  <Typography fontWeight={950}>
                    {loading ? "..." : `${taxaClientesAtivos}%`}
                  </Typography>
                </Stack>

                <LinearProgress
                  variant="determinate"
                  value={loading ? 0 : taxaClientesAtivos}
                  sx={{ height: 9, borderRadius: 99 }}
                />

                <Divider />

                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary" fontWeight={800}>
                    Próximas renovações
                  </Typography>
                  <Typography fontWeight={950}>
                    {loading ? "..." : clientesInfo.proximos30}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary" fontWeight={800}>
                    Clientes expirados
                  </Typography>
                  <Typography fontWeight={950}>
                    {loading ? "..." : clientesInfo.expirados}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary" fontWeight={800}>
                    Sem email
                  </Typography>
                  <Typography fontWeight={950}>
                    {loading ? "..." : clientesInfo.semEmail}
                  </Typography>
                </Stack>
              </Stack>
            </Section>
          </Grid>
        </Grid>

        <Box sx={{ mt: 2.2 }}>
          <Section title="Resumo Operacional" subtitle="Indicadores importantes da plataforma">
            <Grid container spacing={2}>
              {operationalCards.map((card) => (
                <Grid item xs={12} md={4} key={card.title}>
                  <OperationalBox {...card} />
                </Grid>
              ))}
            </Grid>
          </Section>
        </Box>
      </Box>
    </Box>
  );
}